import React, { useState, useEffect } from 'react';
import './App.css';
// import AWSIcon from './components/AWSIcon';
import CodeTypingDemo from './components/CodeTypingDemo';
import TypingAnimation from './components/TypingAnimation';
import CounterAnimation from './components/CounterAnimation';
import ScrollProgress from './components/ScrollProgress';

const App = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // const [visibleService, setVisibleService] = useState(null);


  const awsServiceMap = {
  "AWS EMR": "emr",
  "Snowflake": "snowflake",
  "Python": "lambda",
  "AWS Glue": "glue",
  "AWS Airflow": "airflow",
  "Apache Kafka": "kinesis",
  "Apache Spark": "emr-spark",
  "Iceberg": "athena"
};

  useEffect(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved) setDarkMode(JSON.parse(saved));
  }, []);

  useEffect(() => {
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'skills', 'achievements', 'experience', 'education', 'projects', 'contact'];
      const scrollPosition = window.scrollY + 100;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const achievements = [
    {
      title: "Permit To Operate Framework",
      impact: "Streamlined production deployment process",
      description: "Designed framework using Microsoft Power Platform for seamless handover from development to production",
      technologies: ["Power Automate", "Microsoft Lists", "Microsoft Forms"],
      award: "Q1 2025 Excellence Award"
    },
    {
      title: "EMR and Snowflake Auto Scaling",
      impact: "$24,500/month cost savings",
      description: "Implemented automated scaling solutions for AWS EMR clusters and Snowflake warehouses, resulting in combined monthly savings of $24,500",
      technologies: ["AWS EMR", "Snowflake", "Python", "Automation"],
      award: "Q4 2024 Excellence Award"
    },
    {
      title: "Project Toggle DR Activity",
      impact: "6-month disaster recovery migration",
      description: "Led scripting efforts for US East to US Central region migration, automating connection string updates",
      technologies: ["Python", "IBM DataStage", "Automation"],
      award: "Q1 2024 Excellence Award"
    },
    {
      title: "UD vs SF Process Automation",
      impact: "90% effort reduction (5+ hrs to 30 mins daily)",
      description: "Automated manual verification process between Snowflake Database and IVR system using Selenium and Python",
      technologies: ["Python", "Selenium", "Snowflake", "lxml"]
    },
    {
      title: "OMNI Data Transfer Optimization",
      impact: "96.7% time reduction (10 hrs to 20 mins)",
      description: "Optimized 400GB file transfers by rerouting through EMR with optimization algorithms",
      technologies: ["AWS EMR", "Data Optimization", "Python"]
    },
    {
      title: "Morningstar ETL Optimization",
      impact: "800+ incidents prevented in 3 months",
      description: "Redesigned preprocessing job architecture to prevent cascade failures across 134 downstream jobs",
      technologies: ["ETL", "Snowflake", "Data Architecture"]
    },
    {
      title: "Database Ingestion Optimization",
      impact: "95% runtime reduction (10 hrs to 30 mins)",
      description: "Converted full table loads to incremental processing for 100M+ record tables",
      technologies: ["SQL", "ETL", "Data Engineering"]
    },
    {
      title: "Kafka Topic Performance Enhancement",
      impact: "90% time reduction (6 hrs to 35 mins)",
      description: "Resolved monthly bottleneck by implementing parallel processing with additional partitions",
      technologies: ["Apache Kafka", "Oracle DB", "Stream Processing"]
    },
    {
      title: "AWS Airflow DAGs Development",
      impact: "Modern ETL pipeline implementation",
      description: "Built data pipelines using AWS Glue operators for S3 to Iceberg table transformations",
      technologies: ["AWS Airflow", "AWS Glue", "Iceberg", "Python"]
    }
    // {
    //   title: "EMR Version Migration",
    //   impact: "Critical infrastructure upgrade",
    //   description: "Led migration from EMR 5.32 to 7.7, including Spark v3 and Python 3.14 upgrades",
    //   technologies: ["AWS EMR", "Apache Spark", "Python", "SDLC"]
    // }
  ];

  const skills = {
    "Languages": [
      { name: "Python", level: "Expert", percentage: 95 },
      { name: "SQL", level: "Advanced", percentage: 90 },
      { name: "JavaScript", level: "Expert", percentage: 95 },
      { name: "ReactJS", level: "Expert", percentage: 95 },
      { name: "Bash", level: "Advanced", percentage: 85 },
      { name: "C++/C", level: "Intermediate", percentage: 70 }
    ],
    "Cloud & Data": [
      { name: "AWS Services", level: "Advanced", percentage: 85 },
      { name: "Snowflake", level: "Advanced", percentage: 90 },
      { name: "Apache Spark", level: "Advanced", percentage: 85 },
      { name: "Apache Kafka", level: "Intermediate", percentage: 75 },
      { name: "Airflow", level: "Intermediate", percentage: 75 },
      { name: "PostgreSQL/MySQL", level: "Advanced", percentage: 85 }
    ],
    "DevOps & Tools": [
      { name: "Git/GitHub", level: "Intermediate", percentage: 75 },
      { name: "Docker", level: "Intermediate", percentage: 70 },
      { name: "Power Automate", level: "Advanced", percentage: 90 },
      { name: "Jenkins/CloudBees", level: "Intermediate", percentage: 70 },
      { name: "Splunk", level: "Intermediate", percentage: 70 },
      { name: "ServiceNow", level: "Intermediate", percentage: 70 }
    ]
  };

  const achievementsWithServices = achievements.map(achievement => ({
  ...achievement,
  awsServices: achievement.technologies
    .filter(tech => awsServiceMap[tech])
    .map(tech => awsServiceMap[tech])
}));

  const projects = [
    {
      title: "LLM-Based Fake News Detection",
      description: "End-to-end data science workflow using Deepseek 8B LLM for content classification",
      technologies: ["Python", "LLM", "Machine Learning"],
      github: "https://github.com/brendanlucas01/llm_based_fake_news_detection"
    },
    {
      title: "Automated Heart Monitoring System ⭐",
      description: "Affordable heart monitoring using MAX30100 & AD8232 sensors with Raspberry Pi",
      technologies: ["Python", "Raspberry Pi", "IoT", "Healthcare"],
      github: "https://github.com/brendanlucas01/Automated-Heart-Monitoring-System"
    },
    {
      title: "Hospital Management System",
      description: "Full-stack application for hospital operations management",
      technologies: ["ReactJS", "Node.js", "MySQL", "ExpressJS"],
      github: "https://github.com/brendanlucas01/Hospital-Management-System"
    },
    {
      title: "Resume Tailor Bot",
      description: "AI-powered resume optimization system leveraging Anthropic Claude for personalized insights, keyword enhancement, and improved job matching.",
      technologies: ["Python", "AI/ML", "Claude API"],
      github: "https://github.com/brendanlucas01/resume-tailor-bot"
    },
    {
      title: "Product Authentication & Warranty Tracking",
      description: "A NFT powered system to prevent counterfeiting and digital warranty management ",
      technologies: ["ReactJS", "Etherium", "NFTs"],
      github: "https://github.com/brendanlucas01/Product-Authentication-and-Warranty-Tracking-System"
    },
    {
      title: "Automated face recogination based door lock",
      description: "Smart door unlocking system using Facial Recognition, RFID, and voice control.",
      technologies: ["Python", "Deep Learning", "Facial Recognition"],
      github: "https://github.com/brendanlucas01/Automated-face-recogination-based-door-lock"
    }
  ];

  return (
    <div className={`app ${darkMode ? 'dark' : ''}`}>
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-brand">Brendan Lucas</div>
          <button 
            className="menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <ul className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
            {['home', 'about', 'skills', 'achievements', 'experience', 'education', 'projects', 'contact'].map((section) => (
              <li key={section}>
                <button
                  onClick={() => scrollToSection(section)}
                  className={activeSection === section ? 'active' : ''}
                >
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </button>
              </li>
            ))}
          </ul>
          <button className="theme-toggle" onClick={() => setDarkMode(!darkMode)}>
            {darkMode ? '☀️' : '🌙'}
          </button>
        </div>
      </nav>
      <ScrollProgress />

      {/* <section id="home" className="hero">
        <div className="hero-content">
          <h1>Brendan Lucas</h1>
          <h2>Data Engineer & Site Reliability Engineer</h2>
          <p className="tagline">Platform engineer specializing in AWS infrastructure, data pipeline optimization, and cost-effective automation solutions</p>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-value">$24.5K</span>
              <span className="stat-label">Monthly Savings</span>
            </div>
            <div className="stat">
              <span className="stat-value">3</span>
              <span className="stat-label">Excellence Awards</span>
            </div>
            <div className="stat">
              <span className="stat-value">90%</span>
              <span className="stat-label">Max Effort Reduction</span>
            </div>
          </div> */}
          <section id="home" className="hero">
            <div className="hero-content">
              <h1>Brendan Lucas</h1>
              <h2><TypingAnimation /></h2>
              <p className="tagline">Solving complex data challenges with elegant automation, smart scaling, and reliability-first engineering</p>
              <div className="hero-stats">
                <div className="stat">
                  <span className="stat-value">
                    $<CounterAnimation end={24.5} duration={5000} suffix="K" />
                  </span>
                  <span className="stat-label">Monthly Savings</span>
                </div>
                <div className="stat">
                  <span className="stat-value">
                    <CounterAnimation end={3} duration={3000} />
                  </span>
                  <span className="stat-label">Excellence Awards</span>
                </div>
                <div className="stat">
                  <span className="stat-value">
                    <CounterAnimation end={90} duration={6000} suffix="%" />
                  </span>
                  <span className="stat-label">Max Effort Reduction</span>
                </div>
              </div>
          <div className="hero-cta">
            <button onClick={() => scrollToSection('contact')} className="btn-primary">
              Contact Me
            </button>
            <a href="https://drive.google.com/uc?id=1wtciJCZFRhaEZxIBoMKr8LYrv47DBynN" download className="btn-secondary">
              Download Resume
            </a>
          </div>
        </div>
      </section>

      <section id="about" className="about">
        <div className="container">
          <h2>About Me</h2>
          <div className="about-content">
            <div className="about-text">
              <p>
                Platform engineer embedded in the Retirement Solutions data platform team, responsible for the 
                reliability, performance, and cost optimization of AWS-based data infrastructure supporting 
                critical financial operations.
              </p>
              <p>
                In just 2+ years at TIAA Global Capabilities, I've delivered solutions that save $24,500 monthly, 
                reduced processing times by up to 96%, and prevented hundreds of production incidents. 
                My expertise spans cloud infrastructure, data engineering, and automation, with a proven track 
                record of transforming complex challenges into efficient, scalable solutions.
              </p>
              <div className="about-highlights">
                <div className="highlight">
                  <span className="icon">🎯</span>
                  <span>AWS Certified (Solutions Architect & Data Engineer)</span>
                </div>
                <div className="highlight">
                  <span className="icon">📍</span>
                  <span>Mumbai / Pune, India</span>
                </div>
                <div className="highlight">
                  <span className="icon">🎓</span>
                  <span>B.E. Computer Engineering (CGPA: 9.22/10)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="demo-section">
        <div className="container">
          <h2>AWS Services in Action</h2>
          <p style={{ textAlign: 'center', marginBottom: '2rem', opacity: 0.8 }}>
            Watch how I leverage various AWS services to build scalable solutions
          </p>
          <CodeTypingDemo />
        </div>
      </section>

      <section id="skills" className="skills">
        <div className="container">
          <h2>Technical Skills</h2>
          <div className="skills-grid">
            {Object.entries(skills).map(([category, items]) => (
              <div key={category} className="skill-category">
                <h3>{category}</h3>
                <div className="skill-items">
                  {items.map((skill) => (
                    <div key={skill.name} className="skill-item">
                      <div className="skill-header">
                        <span className="skill-name">{skill.name}</span>
                        <span className="skill-level">{skill.level}</span>
                      </div>
                      <div className="skill-bar">
                        <div 
                          className="skill-progress"
                          style={{ width: `${skill.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      


      <section id="achievements" className="achievements">
        {/* <AWSIcon service={visibleService} isVisible={!!visibleService} /> */}
        <div className="container">
          <h2>Key Achievements</h2>
          <div className="achievements-grid">
            {achievementsWithServices.map((achievement, index) => (
              <div 
                key={index} 
                className="achievement-card"
                // onMouseEnter={() => {
                //   if (achievement.awsServices.length > 0) {
                //     // console.log(achievement.awsServices)
                //     setVisibleService(achievement.awsServices[0]);
                //   }
                // }}
                // onMouseLeave={() => setVisibleService(null)}
              >
                <div className="achievement-header">
                  <h3>{achievement.title}</h3><br/>
                  {achievement.award && (
                    <span className="award-badge">🏆 {achievement.award}</span>
                  )}
                </div>
                <div className="achievement-impact">{achievement.impact}</div>
                <p className="achievement-description">{achievement.description}</p>
                {/* <AWSIcon service={visibleService} isVisible={!!visibleService} /> */}
                <div className="achievement-tech">
                  {achievement.technologies.map((tech) => (
                    <span 
                      key={tech} 
                      className="tech-tag"
                      // onMouseEnter={() => {
                      //   if (awsServiceMap[tech]) {
                      //     setVisibleService(awsServiceMap[tech]);
                      //   }
                      // }}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="experience" className="experience">
        <div className="container">
          <h2>Work Experience</h2>
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-date">Jul 2023 - Present</div>
              <div className="timeline-content">
                <h3>Data Engineer & Site Reliability Engineer</h3>
                <h4>TIAA Global Capabilities</h4>
                <p className="timeline-location">Pune, India</p>
                <p>
                  Platform engineer responsible for AWS infrastructure optimization, data pipeline development, 
                  and process automation. Delivered solutions resulting in $24,500 monthly savings and 
                  received 3 excellence awards for outstanding contributions.
                </p>
                <ul>
                  <li>🏆 3x Excellence Award recipient (Q1 2024, Q4 2024, Q1 2025)</li>
                  <li>Architected auto-scaling solutions for EMR clusters and Snowflake warehouses</li>
                  <li>Led critical infrastructure migrations including EMR upgrade and disaster recovery</li>
                  <li>Optimized data pipelines reducing processing time by up to 96%</li>
                  <li>Developed automation frameworks eliminating 90% of manual effort</li>
                </ul><br/>
                <a href="https://www.linkedin.com/in/brendanlucas01/" target="_blank" rel="noopener noreferrer" className="project-link">
                      View on LinkedIn →
                </a>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-date">Jun - Jul 2022</div>
              <div className="timeline-content">
                <h3>Software Developer Intern</h3>
                <h4>TIAA Global Capabilities</h4>
                <p className="timeline-location">Pune, India</p>
                <p>
                  Developed critical production support tools and impressed leadership with technical capabilities, 
                  earning a full-time return offer.
                </p>
                <ul>
                  <li>Created automated environment replication tool for production support, reducing setup time from hours to minutes</li>
                  <li>Enabled seamless production-to-lower environment cloning with minimal user input</li>
                  <li>Strong performance led to full-time return offer for 2023</li>
                </ul>
                <div className="tech-stack">
                  <span className="tech-tag">Java</span>
                  <span className="tech-tag">Shell Scripting</span>
                  <span className="tech-tag">DevOps Tools</span>
                </div><br/>
                <a href="https://www.linkedin.com/in/brendanlucas01/" target="_blank" rel="noopener noreferrer" className="project-link">
                      View on LinkedIn →
                </a>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-date">Apr - Jun 2021</div>
              <div className="timeline-content">
                <h3>Web Development Intern</h3>
                <h4>e-Yantra Summer Internship Program, IIT Bombay</h4>
                <p className="timeline-location">Remote</p>
                <p>
                  Selected from 500+ applicants for prestigious IIT Bombay summer program. Built predictive 
                  analytics solutions with measurable impact on user engagement.
                </p>
                <ul>
                  <li>Built predictive analytics dashboard using React.js frontend and Node.js backend</li>
                  <li>Integrated ML model for user participation prediction, improving engagement metrics</li>
                  <li>Achieved 30% increase in team retention through data-driven UI/UX enhancements</li>
                </ul>
                <div className="tech-stack">
                  <span className="tech-tag">React.js</span>
                  <span className="tech-tag">Node.js</span>
                  <span className="tech-tag">Machine Learning</span>
                  <span className="tech-tag">Data Visualization</span>
                </div><br/>
                <a href="https://www.linkedin.com/in/brendanlucas01/" target="_blank" rel="noopener noreferrer" className="project-link">
                      View on LinkedIn →
                </a>
              </div>
            </div>
          </div>

          {/* Career Progression Visualization */}
          {/* <div className="career-progression">
            <h3>Career Progression</h3>
            <div className="progression-timeline">
              <div className="progression-line"></div>
              <div className="progression-points">
                <div className="progression-point">
                  <div className="point-dot"></div>
                  <div className="point-info">
                    <p className="point-date">Apr 2021</p>
                    <p className="point-title">IIT Bombay<br/>Internship</p>
                  </div>
                </div>
                <div className="progression-point">
                  <div className="point-dot"></div>
                  <div className="point-info">
                    <p className="point-date">Jun 2022</p>
                    <p className="point-title">TIAA<br/>Internship</p>
                  </div>
                </div>
                <div className="progression-point">
                  <div className="point-dot active"></div>
                  <div className="point-info">
                    <p className="point-date">Jul 2023</p>
                    <p className="point-title">TIAA<br/>Full-time</p>
                  </div>
                </div>
                <div className="progression-point award">
                  <div className="point-dot award"></div>
                  <div className="point-info">
                    <p className="point-date">Q1 2024</p>
                    <p className="point-title">1st Excellence<br/>Award 🏆</p>
                  </div>
                </div>
                <div className="progression-point award">
                  <div className="point-dot award"></div>
                  <div className="point-info">
                    <p className="point-date">Q4 2024</p>
                    <p className="point-title">2nd Excellence<br/>Award 🏆</p>
                  </div>
                </div>
                <div className="progression-point award">
                  <div className="point-dot award"></div>
                  <div className="point-info">
                    <p className="point-date">Q1 2025</p>
                    <p className="point-title">3rd Excellence<br/>Award 🏆</p>
                  </div>
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </section>

      <section id="education" className="education">
        <div className="container">
          <h2>Education & Certifications</h2>
          <div className="education-grid">
            <div className="education-card">
              <h3>B.E. Computer Engineering</h3>
              <h4>Fr. Conceicao Rodrigues College of Engineering (Fr. CRCE)</h4>
              <p>Mumbai University | 2019 - 2023</p>
              <p className="gpa">CGPA: 9.22/10</p>
              <br/>
              <div className="coursework">
                <h5>Relevant Coursework:</h5>
                <p>Data Structures & Algorithms, Machine Learning, Big Data Analytics, 
                   Distributed Computing, Cloud Computing, Database Systems</p>
              </div>
            </div>
            <div className="certification-section">
              <h3>Certifications</h3>
              <div className="certifications">
                <div className="cert-item">
                  <span className="cert-icon">🎖️</span>
                  <div>
                    <h4>AWS Certified Solutions Architect – Associate</h4>
                    <p>October 2025</p>
                    <a href="https://cp.certmetrics.com/amazon/en/public/verify/credential/3fc5fe901c934088a6bb25f6a31a3c3f" target="_blank" rel="noopener noreferrer" className="project-link">
                      View on CertiMetrics →
                    </a>
                  </div>
                </div>
                <div className="cert-item">
                  <span className="cert-icon">🎖️</span>
                  <div>
                    <h4>AWS Certified Data Engineer – Associate</h4>
                    <p>Expected: November 2025</p>
                    {/* <a href="" target="_blank" rel="noopener noreferrer" className="project-link">
                      View on CertiMetrics →
                    </a> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="projects" className="projects">
        <div className="container">
          <h2>Featured Projects</h2>
          <div className="projects-grid">
            {projects.map((project, index) => (
              <div key={index} className="project-card">
                <h3>{project.title}</h3>
                <p>{project.description}</p>
                <div className="project-tech">
                  {project.technologies.map((tech) => (
                    <span key={tech} className="tech-tag">{tech}</span>
                  ))}
                </div>
                <a href={project.github} target="_blank" rel="noopener noreferrer" className="project-link">
                  View on GitHub →
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="contact">
        <div className="container">
          <h2>Get In Touch</h2>
          <p className="contact-intro">
            I'm interested in opportunities in Site Reliability Engineering, Data Engineering, 
            DevOps, Cloud Architecture, and MLOps. Let's discuss how I can help optimize your infrastructure 
            and reduce operational costs.
          </p>
          <div className="contact-links">
            <a href="mailto:brendanlucas01@gmail.com" className="contact-link">
              <span className="icon">📧</span>
              brendanlucas01@gmail.com
            </a>
            <a href="https://linkedin.com/in/brendanlucas01" target="_blank" rel="noopener noreferrer" className="contact-link">
              <span className="icon">💼</span>
              LinkedIn
            </a>
            <a href="https://github.com/brendanlucas01" target="_blank" rel="noopener noreferrer" className="contact-link">
              <span className="icon">💻</span>
              GitHub
            </a>
            {/* <a href="https://know-brendan-lucas.web.app/" target="_blank" rel="noopener noreferrer" className="contact-link">
              <span className="icon">🌐</span>
              Portfolio
            </a> */}
          </div>
        </div>
      </section>

      <footer className="footer">
        <p>&copy; 2024 Brendan Lucas. Built with React & passion for optimization.</p>
      </footer>
    </div>
  );
};

export default App;